import { $, RECHAZO, alcance, aviso, el, esc, fecha, limpiar, num, pedir, puede, rpc, sb, pedirTodo, ordenarPor } from './nucleo';

// =====================================================================
//  EL CONTEO DE STOCK Y LOS DESVÍOS  (0037, punto 6)
// =====================================================================
//  Con el stock quieto después del corte, el picker recorre el depósito y
//  cuenta contra lo que dice el sistema (relevamiento C2). Tres pantallas
//  en una pestaña, cada una con su permiso:
//
//    · El conteo del día ..... `conteo.ver` / `conteo.contar` / `conteo.cerrar`
//    · Los desvíos de stock .. `desvio.ver` / `desvio.resolver`
//    · Ajustar el stock ...... `stock.ajustar`
//
//  Las dos reglas que gobiernan esta pantalla vienen de la base y no se
//  discuten acá:
//
//  D65 · CONTAR NO AJUSTA. El conteo informa: escribe la diferencia y abre
//        el desvío. Corregir el número del sistema es otro acto, con otro
//        permiso y su movimiento firmado. Por eso el ajuste es una tarjeta
//        aparte y no un botón adentro del conteo: un error de conteo no se
//        convierte en stock real porque nadie lo miró.
//
//  D66 · El conteo se cierra completo, o parcial con el motivo escrito. La
//        base rechaza el cierre si falta contar algo, y dice qué falta; la
//        pantalla ofrece entonces el camino del parcial, que es el que se
//        va a usar de verdad cuando algo no se pudo contar.
//
//  Decisión de esta pantalla: **la lista de lo que falta no muestra lo que el sistema espera.**
//  `v_conteo_pendiente` no trae el esperado a propósito: un conteo que dice el número antes de contar no
//  es un conteo. El esperado aparece recién al lado de lo contado.
//
//  Una consulta que falla NO se dice como "no hay nada": "no falta contar nada" invita a cerrar el conteo y
//  "no hay ningún conteo abierto" invita a abrir un segundo. Cada carga mira su `error` y lo muestra.
let CONTEO = null, PENDIENTES = [], CONTADAS = [], DESVIOS = [];
let ANTERIORES = [], CATEGORIAS = [], PRODUCTOS_AJ = [], PERSONAS = [];
//  Qué desvíos ya tienen un ajuste hecho. `null` quiere decir "no se pudo
//  averiguar" —falta `stock.ver`—, que no es lo mismo que "ninguno".
let AJUSTADOS = null;
//  De qué desvío viene lo que hay cargado en el formulario de ajuste, para
//  poder soltarlo si la persona cambia el producto a mano.
let AJUSTE_DE = null;

const CHIP_DESVIO = {
  abierto: 'c-warn', en_revision: 'c-pen', resuelto: 'c-ok', asumido: ''
};
const catDe    = id => CATEGORIAS.find(c => c.id === id)?.nombre || 'Sin categoría';
//  Quien abrió un conteo puede haber sido dado de baja: "abierto por —" es mejor que una frase cortada.
//
//  Ojo con el respaldo (D83): si la lista de personas no se pudo leer, NADIE está en ella y "alguien que ya
//  no está activo" se diría de todos. Las dos situaciones se dicen distinto.
const nombreDe = id => id == null ? null
  : (PERSONAS.find(p => p.id === id)?.nombre
     || (ERROR_PERSONAS ? 'alguien que no pude averiguar quién es'
                        : 'alguien que ya no está activo'));

//  La diferencia, dicha como la va a leer una persona: el signo importa,
//  porque "+2" y "-2" son dos problemas distintos.
const difTxt = d => d === 0 ? '<span class="chip c-ok">da bien</span>'
  : `<span class="chip ${d > 0 ? 'c-pen' : 'c-bad'}">${d > 0 ? '+' : ''}${num(d)}</span>`;

//  D85: el alcance también afirma, y acá DOS veces. El RLS de `conteo_lineas` es
//  `alcance('conteo','ver') = 'todos'` O haber contado la línea uno mismo, así que con alcance `propio`:
//
//  · `v_conteo_actual` (joinea `conteo_lineas` sin `left`) trae sólo lo que contó uno, y
//  · `v_conteo_pendiente` queda INFLADA: el recorte le entra por un `NOT EXISTS`, y lo que contó OTRO
//    vuelve a salir como pendiente y manda a recontar.
//
//  Hoy nadie tiene ese alcance, pero se da suelto desde Usuarios. La pantalla no lo puede arreglar: lo dice.
const veTodoElConteo = () => alcance('conteo','ver') === 'todos';

//  Las unidades son enteras. `type="number"` no lo impide, y mandar 2,5 a un
//  parámetro `int` le muestra a la persona un error crudo de Postgres.
const entero = v => /^-?\d+$/.test(String(v).trim());

// ---------------------------------------------------------------------
//  La carga
// ---------------------------------------------------------------------
//  Regla 4 del README: las funciones `cargar…` sólo TRAEN; se pinta cuando terminaron todas (las tablas
//  se resuelven con listas de otra consulta y, si se pinta antes, se muestran ids).
export async function cargarConteo(){
  ['#conteoMsg','#cierreMsg','#contarMsg','#desviosMsg','#ajusteMsg'].forEach(limpiar);
  //  El cartel del cierre parcial es hermano de `#cierreMsg`: limpiarlo no lo borra, y si sobrevive a una
  //  recarga puede decir "quedan 3 sin contar" cuando ya no falta ninguno.
  document.getElementById('confParcial')?.remove();

  //  Los errores de lectura son de ESTA carga: si no se limpian acá, una
  //  falla vieja seguiría tapando la tabla después de que la red volvió.
  ERROR_PENDIENTES = null; ERROR_CONTADAS = null; ERROR_DESVIOS = null;
  ERROR_ANTERIORES = null; ERROR_PERSONAS = null; ERROR_AJUSTADOS = null;

  const ve      = puede('conteo','ver');
  const cuenta  = puede('conteo','contar');
  const veDesv  = puede('desvio','ver');
  const ajusta  = puede('stock','ajustar');

  $('#cardConteo').classList.toggle('oculto', !ve && !cuenta);
  $('#cardDesviosStock').classList.toggle('oculto', !veDesv);
  $('#cardAjustar').classList.toggle('oculto', !ajusta);
  $('#cardConteosAnt').classList.toggle('oculto', !ve);

  const tareas = [];
  if (ve || cuenta) tareas.push(cargarConteoAbierto(), cargarPersonas());
  if (ve) tareas.push(cargarAnteriores());
  if (ve || cuenta) tareas.push(cargarCategorias());
  if (veDesv) tareas.push(cargarDesvios());
  if (ajusta) tareas.push(cargarProductosAjuste());
  await Promise.all(tareas);

  //  Segunda tanda: lo que necesita el resultado de la primera. Lo contado y lo pendiente dependen de que
  //  exista un conteo abierto, y los ajustes se preguntan sólo por los desvíos que se van a pintar.
  const segunda = [];
  if ((ve || cuenta) && CONTEO) segunda.push(cargarPendientes(), cargarContadas());
  else { PENDIENTES = []; CONTADAS = []; }
  if (veDesv || ajusta) segunda.push(cargarAjustados());
  await Promise.all(segunda);

  //  Un conteo distinto del que se estaba tecleando: lo tecleado no puede reaparecer precargado (pasa si otro
  //  cierra el conteo desde otra máquina y se abre el del día siguiente).
  if ((CONTEO?.id ?? null) !== CONTEO_TECLEADO){
    TECLEADO.clear();
    CONTEO_TECLEADO = CONTEO?.id ?? null;
  }
  //  Y el formulario de ajuste no puede quedar atado a un desvío que ya se
  //  ajustó —o cuyo producto se dio de baja— mientras estaba abierto.
  revalidarAjuste();

  if (ve || cuenta){ pintarConteo(); }
  if (ve) pintarAnteriores();
  if (veDesv) pintarDesvios();
  if (ajusta) pintarProductosAjuste();
}

//  El RLS de `conteos` es `conteo.ver`, distinto de `conteo.contar`: quien tiene sólo el segundo abre el
//  conteo pero no lo puede leer. "Te falta el permiso" es un estado estable (lo arregla un administrador);
//  "no pude leer ahora" puede ser red sobre un conteo que existe, y ahí hay que frenar la mano: abrir un
//  segundo conteo sobre uno abierto es lo peor que puede pasar acá.
let ERROR_CONTEO = null, SIN_VER = false;
//  Lo mismo una pantalla más adentro. `cargarConteoAbierto` cuida la mitad de la frase que invita a abrir un
//  segundo conteo; estas tres cuidan la que invita a CERRARLO: "no falta contar nada", "todavía no se contó
//  nada" y "no hay desvíos de stock" no los puede decir una lectura rota. Mostrar el error en otra tarjeta no
//  alcanza: la tabla seguiría afirmando lo contrario y el botón de cerrar seguiría vivo.
let ERROR_PENDIENTES = null, ERROR_CONTADAS = null, ERROR_DESVIOS = null;
//  Y las dos lecturas que se tragaban su error. La de las personas alimenta un respaldo que HABLA (ver
//  `nombreDe`), y por eso también necesita saberse rota.
let ERROR_ANTERIORES = null, ERROR_PERSONAS = null;
//  Y por qué no se sabe qué desvíos ya se ajustaron: falta el permiso (estable, lo arregla un administrador)
//  o falló la consulta. El cartel no debe decir siempre lo primero: mandaría a pedir un permiso que ya se tiene.
let ERROR_AJUSTADOS = null;
async function cargarConteoAbierto(){
  ERROR_CONTEO = null; SIN_VER = false;
  if (!puede('conteo','ver')){ CONTEO = null; SIN_VER = true; return; }
  const { data, error } = await pedir(sb.from('conteos').select('*')
    .eq('estado','abierto').limit(1).maybeSingle());
  if (error){ CONTEO = null; ERROR_CONTEO = error.message; return; }
  CONTEO = data || null;
}
async function cargarPendientes(){
  const { data, error } = await pedirTodo(() => sb.from('v_conteo_pendiente').select('*'),
    { por: 'producto_id', orden: ordenarPor('producto', 'producto_id') });
  if (error){ PENDIENTES = []; ERROR_PENDIENTES = error.message;
              aviso('#contarMsg','bad', error.message); return; }
  PENDIENTES = data || [];
}
async function cargarContadas(){
  const { data, error } = await pedirTodo(() => sb.from('v_conteo_actual').select('*'),
    { por: 'linea_id', orden: ordenarPor('producto', 'linea_id') });
  if (error){ CONTADAS = []; ERROR_CONTADAS = error.message;
              aviso('#contarMsg','bad', error.message); return; }
  CONTADAS = data || [];
}
async function cargarAnteriores(){
  const { data, error } = await pedir(sb.from('conteos').select('*')
    .neq('estado','abierto').order('fecha', { ascending: false }).limit(30));
  if (error){ ANTERIORES = []; ERROR_ANTERIORES = error.message; return; }
  ANTERIORES = data || [];
}
async function cargarCategorias(){
  const { data, error } = await pedirTodo(() => sb.from('categorias').select('id,nombre'), { por: 'id', orden: ordenarPor('nombre', 'id') });
  //  Sin categorías la lista sigue sirviendo (queda plana), pero se dice: agrupar por categoría es la única
  //  guía de recorrido del picker.
  if (error){ CATEGORIAS = []; aviso('#contarMsg','warn','No se pudieron leer las categorías, así que la lista va sin agrupar: ' + error.message); return; }
  CATEGORIAS = data || [];
}
async function cargarPersonas(){
  const { data, error } = await pedir(sb.from('usuarios').select('id,nombre')
    .eq('activo', true).order('nombre'));
  if (error){ PERSONAS = []; ERROR_PERSONAS = error.message; return; }
  PERSONAS = data || [];
}
async function cargarDesvios(){
  const { data, error } = await pedir(sb.from('v_desvios_stock').select('*')
    .order('detectado_en', { ascending: false }).limit(200));
  if (error){ aviso('#desviosMsg','bad', error.message); DESVIOS = [];
              ERROR_DESVIOS = error.message; return; }
  DESVIOS = data || [];
}
//  Un desvío ya ajustado no se vuelve a ajustar: la `cantidad` del desvío es histórica y no baja al
//  corregir, así que dos personas pueden ajustar dos veces y dejar el stock peor que antes del conteo.
//
//  OJO CON EL RLS: una política que tapa una tabla NO devuelve error, devuelve cero filas y `error: null`:
//  "no me dejan mirar" llega igual que "no hay ninguno". Por eso el permiso se pregunta ACÁ, antes de
//  consultar (igual que `cargarConteoAbierto`): si no, `AJUSTADOS` sería un Set vacío y la pantalla juraría
//  que ningún desvío se ajustó.
//
//  Se pregunta sólo por los desvíos en pantalla: sin acotar, el histórico entero pasaría el tope de filas
//  de PostgREST y se truncaría en silencio, perdiendo los ajustes más nuevos.
async function cargarAjustados(){
  ERROR_AJUSTADOS = null;
  if (!puede('stock','ver')){ AJUSTADOS = null; return; }
  //  Si los desvíos no se pudieron leer, `DESVIOS` está vacío por un error: un Set vacío diría "ninguno se
  //  ajustó" sobre una lista que nadie pudo mirar.
  //
  //  OJO: hoy esto no cambia nada en pantalla, a propósito: `pintarDesvios` corta antes cuando
  //  `ERROR_DESVIOS` está puesto y `revalidarAjuste` trata `null` como Set vacío, así que ninguna prueba
  //  lo distingue. Es correcto igual: si ese `return` temprano cambia, "ninguno" vs "no sé" vuelve a importar.
  if (ERROR_DESVIOS){ AJUSTADOS = null; ERROR_AJUSTADOS = ERROR_DESVIOS; return; }
  const ids = DESVIOS.map(d => d.id);
  if (!ids.length){ AJUSTADOS = new Set(); return; }
  //  El `tipo` va en el filtro porque la base lo pregunta: `ajustar_stock` sólo cuenta como "ya ajustado" un
  //  movimiento de ajuste. Sin esta línea la pantalla escondería el botón por un movimiento de otra clase y
  //  la base lo dejaría pasar: dos reglas parecidas escritas distinto terminan diciendo lo contrario.
  const { data, error } = await pedir(sb.from('movimientos_stock')
    .select('referencia_id').eq('tipo','ajuste')
    .eq('referencia_tipo','desvio').in('referencia_id', ids));
  if (error){ AJUSTADOS = null; ERROR_AJUSTADOS = error.message; return; }
  AJUSTADOS = new Set((data || []).map(m => m.referencia_id));
}
async function cargarProductosAjuste(){
  const { data, error } = await pedirTodo(() => sb.from('productos').select('id,codigo,descripcion')
    .eq('activo', true), { por: 'id', orden: ordenarPor('codigo', 'id') });
  if (error){ PRODUCTOS_AJ = []; aviso('#ajusteMsg','bad', error.message); return; }
  PRODUCTOS_AJ = data || [];
}

// ---------------------------------------------------------------------
//  El conteo del día
// ---------------------------------------------------------------------
function pintarConteo(){
  const cuenta = puede('conteo','contar');
  const cierra = puede('conteo','cerrar');

  if (!CONTEO){
    //  Un error de lectura NO se dice como "no hay conteo": eso invita a
    //  abrir un segundo conteo sobre uno que ya existe.
    $('#conteoEstado').innerHTML = ERROR_CONTEO
      ? `<div class="aviso a-bad">No se pudo leer el conteo del día, así que no sé si
           hay uno abierto. <b>No abras otro hasta saberlo.</b><br>${esc(ERROR_CONTEO)}</div>`
      : SIN_VER
      ? `<div class="aviso a-warn">Te falta el permiso <b>Ver los conteos</b>
           (<span class="mono">conteo.ver</span>), así que esta pantalla no te puede mostrar
           el conteo del día ni la lista de lo que falta contar, aunque sí puedas cargarlo.
           Pedile a un administrador que te lo agregue: sin eso, acá no vas a ver nada.</div>`
      : `<div class="aviso a-info">No hay ningún conteo abierto.
         ${cuenta ? 'Abrí el del día cuando el depósito esté quieto y empezá a recorrer.'
                  : 'Cuando el picker abra el del día, lo vas a ver acá.'}</div>`;
    //  Con el permiso faltante el botón sigue vivo —`abrir_conteo` funciona y
    //  contesta—; con un error de lectura no, porque no sabemos si ya hay uno.
    $('#btnAbrirConteo').classList.toggle('oculto', !cuenta || !!ERROR_CONTEO);
    $('#cardContar').classList.add('oculto');
    $('#cardContado').classList.add('oculto');
    return;
  }

  const total = PENDIENTES.length + CONTADAS.length;
  const dif   = CONTADAS.filter(l => l.diferencia !== 0).length;
  const abrio = nombreDe(CONTEO.abierto_por);
  //  Un número salido de una lista vacía por un error de lectura no es un número: "Falta contar 0" con la
  //  consulta rota es la misma mentira que "No falta contar nada".
  const nose   = '<b class="hint">no se pudo leer</b>';
  const noLeido = [ERROR_PENDIENTES && 'lo que falta contar',
                   ERROR_CONTADAS   && 'lo contado'].filter(Boolean).join(' ni ');
  $('#conteoEstado').innerHTML = `
    <div class="aviso a-ok">Conteo del <b>${esc(String(CONTEO.fecha))}</b>, abierto${
      abrio ? ` por ${esc(abrio)}` : ''}.</div>
    ${noLeido ? `<div class="aviso a-bad" style="margin-top:0.588rem">No se pudo leer ${noLeido},
         así que este conteo se está mostrando incompleto.
         <b>No lo cierres hasta saberlo.</b></div>` : ''}
    ${veTodoElConteo() ? '' : `<div class="aviso a-warn" style="margin-top:0.588rem">
         Tu permiso de ver conteos alcanza <b>sólo lo que contaste vos</b>, así que estos
         números son los tuyos: lo que contaron otros te aparece como si faltara.</div>`}
    <div class="grid g4" style="margin-top:0.588rem">
      <div><div class="hint">Contados</div>${ERROR_CONTADAS ? nose
            : `<b>${num(CONTADAS.length)}</b> de ${ERROR_PENDIENTES ? '?' : num(total)}`}</div>
      <div><div class="hint">Falta contar</div>${
            ERROR_PENDIENTES ? nose : `<b>${num(PENDIENTES.length)}</b>`}</div>
      <div><div class="hint">No dieron</div>${ERROR_CONTADAS ? nose : `<b>${num(dif)}</b>`}</div>
      <div><div class="hint">Nota del conteo</div>${esc(CONTEO.notas || '—')}</div>
    </div>`;
  $('#btnAbrirConteo').classList.add('oculto');
  $('#cardContar').classList.toggle('oculto', !cuenta);
  $('#cardContado').classList.remove('oculto');
  //  Cerrar sin saber qué falta es cerrar a ciegas: el cierre PARCIAL escribiría "se cerró igual" sobre una
  //  lista que puede no ser la real. Con la lectura rota, el botón no está.
  $('#btnCerrarConteo').classList.toggle('oculto', !cierra || !!noLeido);
  pintarPendientes();
  pintarContadas();
}

//  Lo que falta, agrupado por categoría. No hay orden de recorrido como dato (no existe la ubicación física):
//  la categoría es lo más parecido a "andá a esa estantería".
//
//  Los números ya tecleados se conservan al repintar: el picker carga ocho filas y recién ahí guarda, y si
//  el repintado (o una letra en el filtro) le vacía las otras siete puede creer que guardó ocho.
//  Viven ACÁ, no en el DOM: leerlos del DOM sólo salva los que están a la vista, y filtrar y volver perdía
//  los de las filas sacadas de la tabla.
const TECLEADO = new Map();
//  De qué conteo son los números tecleados. Si el conteo cambia, se olvidan: uno de ayer no puede reaparecer
//  precargado hoy y guardarse como conteo físico.
let CONTEO_TECLEADO = null;
$('#tblPendientes').oninput = e => {
  const i = el(e.target).closest('[data-cant]');
  if (!i) return;
  if (i.value === '') TECLEADO.delete(i.dataset.cant);
  else TECLEADO.set(i.dataset.cant, i.value);
};
function pintarPendientes(){
  if (ERROR_PENDIENTES){
    $('#tblPendientes').innerHTML = `<tr><td colspan="3"><div class="aviso a-bad">
      No se pudo leer lo que falta contar, así que <b>no sé si falta algo</b>.
      No cierres el conteo hasta saberlo.<br>${esc(ERROR_PENDIENTES)}</div></td></tr>`;
    return;
  }
  const q = ($('#buscaPend').value || '').toLowerCase();
  const f = PENDIENTES.filter(p => !q ||
    (p.producto||'').toLowerCase().includes(q) ||
    (p.descripcion||'').toLowerCase().includes(q));

  if (!f.length){
    //  "Se puede cerrar completo" no anda con el alcance recortado: la base cuenta las líneas de TODOS y
    //  rebota el cierre completo con `conteo_incompleto`.
    $('#tblPendientes').innerHTML = PENDIENTES.length
      ? '<tr><td colspan="3" class="vacio">Ningún producto pendiente coincide con el filtro</td></tr>'
      : veTodoElConteo()
      ? '<tr><td colspan="3" class="vacio">No falta contar nada. El conteo se puede cerrar completo.</td></tr>'
      : '<tr><td colspan="3" class="vacio">No te falta contar nada a vos. Lo que hayan contado otros no lo podés ver desde acá.</td></tr>';
    return;
  }

  const grupos = {};
  f.forEach(p => { (grupos[catDe(p.categoria_id)] ||= []).push(p); });

  $('#tblPendientes').innerHTML = Object.keys(grupos).sort().map(cat => `
    <tr class="sub"><td colspan="3"><b>${esc(cat)}</b>
        <span class="hint">— ${num(grupos[cat].length)} sin contar</span></td></tr>` +
    grupos[cat].map(p => `
    <tr><td><span class="mono">${esc(p.producto)}</span> ${esc(p.descripcion)}</td>
        <td><input type="number" min="0" step="1" data-cant="${p.producto_id}"
                   value="${esc(TECLEADO.get(String(p.producto_id)) ?? '')}"
                   placeholder="cuántas hay" style="max-width:7.647rem"></td>
        <td><div class="fila-acc">
              <button class="b b-pri" data-contar="${p.producto_id}">Guardar</button>
            </div></td></tr>`).join('')
  ).join('');
}

function pintarContadas(){
  if (ERROR_CONTADAS){
    $('#tblContadas').innerHTML = `<tr><td colspan="7"><div class="aviso a-bad">
      No se pudo leer lo contado. <b>No quiere decir que no se haya contado nada.</b>
      <br>${esc(ERROR_CONTADAS)}</div></td></tr>`;
    return;
  }
  $('#tblContadas').innerHTML = CONTADAS.map(l => `
    <tr><td><span class="mono">${esc(l.producto)}</span> ${esc(l.descripcion)}</td>
        <td class="num"><b>${num(l.contado)}</b></td>
        <td class="num">${num(l.esperado)}</td>
        <td>${difTxt(l.diferencia)}</td>
        <td>${esc(l.notas || '—')}</td>
        <td>${esc(l.conto || '—')}<div class="hint">${fecha(l.contado_en)}</div></td>
        <td>${puede('conteo','contar')
              ? `<div class="fila-acc"><button class="b b-sec" data-recontar="${l.producto_id}">Recontar</button></div>`
              : ''}</td></tr>
  `).join('') || `<tr><td colspan="7" class="vacio">${veTodoElConteo()
      ? 'Todavía no se contó nada'
      : 'Vos todavía no contaste nada. Lo que hayan contado otros no lo podés ver desde acá.'}</td></tr>`;
}

$('#buscaPend').oninput = pintarPendientes;

$('#btnAbrirConteo').onclick = async () => {
  limpiar('#conteoMsg');
  const btn = $('#btnAbrirConteo'); btn.disabled = true;
  try {
    const { data, error } = await rpc('abrir_conteo', {
      p_notas: $('#coNotas').value.trim() || null });
    if (error){ aviso('#conteoMsg','bad', error.message); return; }
    $('#coNotas').value = '';
    await cargarConteo();
    aviso('#conteoMsg', data.nueva ? 'ok' : 'info', data.mensaje);
  } finally { btn.disabled = false; }
};

// ---------------------------------------------------------------------
//  Contar
// ---------------------------------------------------------------------
$('#tblPendientes').onclick = async e => {
  const b = el(e.target).closest('[data-contar]');
  if (!b) return;
  const id  = Number(b.dataset.contar);
  //  El input se busca DENTRO de esta tabla, no en toda la página: dos
  //  pantallas con el mismo atributo no pueden pisarse.
  const inp = $(`#tblPendientes [data-cant="${id}"]`);
  const val = inp ? inp.value.trim() : '';
  limpiar('#contarMsg');
  //  Vacío no es cero. Cero es "fui, miré y no hay ninguna", que es un dato
  //  y probablemente un desvío; vacío es "todavía no lo conté".
  if (val === ''){
    aviso('#contarMsg','bad','Escribí cuántas unidades hay. Si no hay ninguna, es un cero, y eso es distinto de dejarlo en blanco.');
    return;
  }
  if (!entero(val) || Number(val) < 0){
    aviso('#contarMsg','bad','Las unidades se cuentan de a una: escribí un número entero, y no puede ser negativo.');
    return;
  }
  await contar(id, Number(val), null, b);
};

async function contar(producto_id, contado, notas, btn){
  if (btn) btn.disabled = true;
  try {
    const { data, error } = await rpc('contar', {
      p_producto_id: producto_id, p_contado: contado, p_notas: notas });
    if (error){ aviso('#contarMsg','bad', error.message); return; }
    //  Ya está guardado en la base: deja de ser un número tecleado a medias.
    TECLEADO.delete(String(producto_id));
    await cargarConteo();
    //  La base contesta si dio bien o por cuánto no dio. Es lo que le permite
    //  al picker volver a mirar el estante ahora, y no mañana.
    aviso('#contarMsg', data.diferencia === 0 ? 'ok' : 'warn', data.mensaje);
  } finally { if (btn) btn.disabled = false; }
}

//  Recontar es volver a cargar el mismo producto: `contar` pisa la línea
//  anterior. Se hace con una fila desplegable para que el número viejo
//  quede a la vista mientras se escribe el nuevo.
$('#tblContadas').onclick = e => {
  const b = el(e.target).closest('[data-recontar]');
  if (!b) return;
  const l = CONTADAS.find(x => x.producto_id === Number(b.dataset.recontar));
  if (!l) return;
  const fila = b.closest('tr');
  if (fila.nextElementSibling?.id === 'filaRecontar'){ fila.nextElementSibling.remove(); return; }
  document.getElementById('filaRecontar')?.remove();

  const tr = document.createElement('tr');
  tr.id = 'filaRecontar';
  tr.innerHTML = `<td colspan="7">
    <div class="confirmar">
      <div class="conf-tit">Recontar ${esc(l.producto)} — ${esc(l.descripcion)}</div>
      <p class="conf-txt">Habías contado <b>${num(l.contado)}</b>. Lo que escribas ahora
         reemplaza esa línea; el conteo todavía no se cerró, así que no hay nada que deshacer.</p>
      <div class="fila-acc">
        <input type="number" min="0" step="1" id="rcCant" value="${l.contado}" style="max-width:7.647rem">
        <input id="rcNotas" placeholder="Nota, opcional (ej. estaban en dos cajas)">
        <button class="b b-sec" id="rcCancelar">Cancelar</button>
        <button class="b b-pri" id="rcOk">Guardar</button>
      </div>
    </div></td>`;
  fila.after(tr);
  $('#rcCancelar', tr).onclick = () => tr.remove();
  $('#rcOk', tr).onclick = async () => {
    const v = $('#rcCant', tr).value.trim();
    limpiar('#contarMsg');
    if (v === ''){ aviso('#contarMsg','bad','Escribí cuántas unidades hay.'); return; }
    if (!entero(v) || Number(v) < 0){
      aviso('#contarMsg','bad','Las unidades se cuentan de a una: escribí un número entero, y no puede ser negativo.');
      return;
    }
    const notas = $('#rcNotas', tr).value.trim() || null;
    tr.remove();
    await contar(l.producto_id, Number(v), notas, null);
  };
};

// ---------------------------------------------------------------------
//  Cerrar el conteo  (D66)
// ---------------------------------------------------------------------
//  Se intenta cerrar completo. Si falta contar algo, la base lo rechaza y dice QUÉ falta: recién ahí se
//  ofrece el parcial, con el motivo obligatorio. El parcial es la excepción, no el botón de entrada.
$('#btnCerrarConteo').onclick = async () => {
  limpiar('#cierreMsg');
  document.getElementById('confParcial')?.remove();
  const btn = $('#btnCerrarConteo'); btn.disabled = true;
  try {
    const { data, error } = await rpc('cerrar_conteo', {
      p_parcial: false, p_motivo: null, p_notas: $('#ccNotas').value.trim() || null });
    if (!error){
      $('#ccNotas').value = '';
      await cargarConteo();
      aviso('#cierreMsg', data.con_diferencia ? 'warn' : 'ok', data.mensaje);
      return;
    }
    //  El único rechazo con salida por pantalla es el de los faltantes: cualquier otro (permiso, no hay conteo
    //  abierto) se muestra y listo. El de los faltantes va adentro del cartel, donde está el botón para resolverlo.
    //
    //  Se reconoce por el `hint` que manda la base (0039, D68), no por el texto del mensaje: el texto puede
    //  reescribirse y el cierre parcial dejaría de ofrecerse sin que nada falle a la vista. El mensaje se
    //  sigue mostrando (trae la cuenta y los códigos), pero no decide nada.
    if (error.hint !== 'conteo_incompleto'){
      aviso('#cierreMsg','bad', error.message);
      return;
    }
    ofrecerParcial(error.message);
  } finally { btn.disabled = false; }
};

//  El cartel repite el mensaje de la base (cuenta y lista de códigos) en vez de contar por su cuenta: el
//  número que vale es el de la base, no el de la última lista traída.
function ofrecerParcial(mensajeDeLaBase){
  if (document.getElementById('confParcial')) return;
  const div = document.createElement('div');
  div.id = 'confParcial';
  div.innerHTML = `
    <div class="confirmar">
      <div class="conf-tit">Cerrarlo como parcial</div>
      <p class="conf-txt">${esc(mensajeDeLaBase || '')}<br><br>
         Un conteo parcial se cierra igual y abre los desvíos de lo que sí se contó, pero
         deja escrito que no se recorrió todo y por qué — un producto que quedó sin contar
         no puede parecerse a uno que dio bien. El motivo es lo que va a leer mañana el que
         mire este conteo.<br><br>
         Si en vez de eso los vas a contar ahora, cerrá este cartel: están en la lista de
         arriba.</p>
      <div class="grid">
        <label>Por qué quedaron sin contar
          <input id="cpMotivo" placeholder="Ej. el pallet de cargadores llegó sin abrir y no se pudo contar">
        </label>
        <div class="fila-acc">
          <button class="b b-sec" id="cpCancelar">Sigo contando</button>
          <button class="b b-pri" id="cpOk">Cerrarlo como parcial</button>
        </div>
      </div>
    </div>`;
  $('#cierreMsg').after(div);
  $('#cpCancelar', div).onclick = () => div.remove();
  $('#cpOk', div).onclick = async () => {
    const motivo = $('#cpMotivo', div).value.trim();
    if (!motivo){
      aviso('#cierreMsg','bad','Escribí por qué quedaron productos sin contar: es lo único que hace que un conteo parcial se entienda después.');
      return;
    }
    const btn = $('#cpOk', div); btn.disabled = true;
    try {
      const { data, error } = await rpc('cerrar_conteo', {
        p_parcial: true, p_motivo: motivo, p_notas: $('#ccNotas').value.trim() || null });
      if (error){ aviso('#cierreMsg','bad', error.message); return; }
      div.remove();
      $('#ccNotas').value = '';
      await cargarConteo();
      //  Si mientras el cartel estaba abierto se contó lo que faltaba, la base cierra COMPLETO aunque se haya
      //  apretado este botón: el aviso dice lo que pasó, no lo que se apretó.
      aviso('#cierreMsg', data.parcial ? 'warn' : 'ok', data.mensaje);
    } finally { btn.disabled = false; }
  };
}

function pintarAnteriores(){
  //  "Todavía no se cerró ningún conteo" es una afirmación sobre el pasado de la empresa y una lectura caída
  //  no la puede hacer: se leería como "nunca se contó".
  if (ERROR_ANTERIORES){
    $('#tblConteosAnt').innerHTML = `<tr><td colspan="6"><div class="aviso a-bad">
      No se pudieron leer los conteos anteriores. <b>No quiere decir que no haya.</b>
      <br>${esc(ERROR_ANTERIORES)}</div></td></tr>`;
    return;
  }
  $('#tblConteosAnt').innerHTML = ANTERIORES.map(c => `
    <tr><td>${esc(String(c.fecha))}</td>
        <td>${c.parcial ? '<span class="chip c-warn">parcial</span>'
                        : '<span class="chip c-ok">completo</span>'}</td>
        <td class="num">${num(c.sin_contar ?? 0)}</td>
        <td class="num">${num(c.con_diferencia ?? 0)}</td>
        <td>${esc(c.motivo_parcial || c.notas || '—')}</td>
        <td>${fecha(c.cerrado_en)}</td></tr>
  `).join('') || '<tr><td colspan="6" class="vacio">Todavía no se cerró ningún conteo</td></tr>';
}

// ---------------------------------------------------------------------
//  Los desvíos de stock
// ---------------------------------------------------------------------
const yaAjustado = d => AJUSTADOS ? AJUSTADOS.has(d.id) : false;

function pintarDesvios(){
  if (ERROR_DESVIOS){
    //  "No hay desvíos de stock" se lee como "el stock está bien", que es
    //  justo lo contrario de lo que sabemos cuando la lectura falla.
    $('#dsNota').innerHTML = '';
    $('#tblDesvios').innerHTML = `<tr><td colspan="8"><div class="aviso a-bad">
      No se pudieron leer los desvíos de stock. <b>No quiere decir que no haya.</b>
      <br>${esc(ERROR_DESVIOS)}</div></td></tr>`;
    return;
  }
  const verCerrados = $('#dsVerCerrados').checked;
  const f = DESVIOS.filter(d => verCerrados || ['abierto','en_revision'].includes(d.estado));
  const resuelve = puede('desvio','resolver');
  const ajusta   = puede('stock','ajustar');

  //  D84 y D86. `v_desvios_stock` sale de `desvios` (lo tapa `desvio.ver`, el permiso de esta tarjeta) pero
  //  llega al producto por un LEFT JOIN a `conteo_lineas`, cuyo RLS pide el alcance `todos` de `conteo.ver`
  //  o haber contado la línea uno mismo. Son dos permisos distintos (el rol `Cajero de envíos` tiene el
  //  primero y no el segundo).
  //
  //  Sin ese alcance la fila sale IGUAL y sólo viene en null lo que sale del conteo (producto y fecha): ni
  //  error, ni lista vacía; ninguna bandera de arriba lo ve.
  //
  //  El null tiene DOS causas que no se dicen igual. Todos los desvíos de stock nacen con
  //  `referencia_tipo = 'conteo_linea'` (los crea `cerrar_conteo`), así que con el alcance completo un
  //  producto en null es que la línea ya no está (código `desvio_sin_producto`); sin el alcance, que no lo
  //  podés ver. "Sin producto" miente en la segunda.
  //  Ojo con cuál falta: `veTodoElConteo()` es falso para quien NO tiene `conteo.ver` **y** para quien lo
  //  tiene con alcance `propio`; decirle a éste "pedíselo a un administrador" lo manda a pedir algo que ya
  //  tiene (regla 16: el cartel que aconseja un camino tiene que dejarlo andando).
  const veElConteo  = veTodoElConteo();
  const tieneConteo = puede('conteo','ver');
  //  Una sola condición para las tres cosas que dependen del producto (cartel, celda y botón): contar con
  //  una y decidir con otra deja el cartel hablando de filas distintas. `producto_id` viene del join tapado.
  const ciegos     = f.filter(d => !d.producto_id).length;

  //  Dos avisos que pueden salir juntos, así que se acumulan en vez de pisarse. Dicen la CAUSA y nada más:
  //  el efecto lo dice cada fila.
  const notas = [];
  //  Si no se pudo averiguar qué desvíos ya se ajustaron, se dice: es la diferencia entre "ninguno" y "no sé".
  if (ajusta && AJUSTADOS === null)
    notas.push(`<div class="aviso a-warn">No pude averiguar cuáles de estos desvíos ya se
      ajustaron ${ERROR_AJUSTADOS
        ? `porque no se pudo leer la lista de movimientos de stock: ${esc(ERROR_AJUSTADOS)}`
        : '(te falta el permiso para ver los movimientos de stock)'}. Fijate antes de
      ajustar, para no corregir dos veces lo mismo.</div>`);
  if (!veElConteo && ciegos)
    notas.push(`<div class="aviso a-warn">${tieneConteo
      ? `Tu permiso de ver conteos alcanza <b>sólo lo que contaste vos</b>, y el producto
         de cada desvío sale de la línea del conteo.`
      : `Te falta el permiso <b>Ver los conteos</b>
         (<span class="mono">conteo.ver</span>), que es de donde sale el producto de cada
         desvío.`} Los ${num(ciegos)} marcados abajo <b>no dicen de qué producto son</b>.
      </div>`);
  $('#dsNota').innerHTML = notas.join('');

  $('#tblDesvios').innerHTML = f.map(d => {
    const hecho    = yaAjustado(d);
    const abierto  = ['abierto','en_revision'].includes(d.estado);
    const ajustable = d.producto_id && ajusta && !hecho;
    return `
    <tr><td class="mono">#${d.id}</td>
        <td>${d.producto_id ? (d.producto
                           ? `<span class="mono">${esc(d.producto)}</span> ${esc(d.producto_nombre || '')}`
                           //  La línea está y el producto no: no puede ser el
                           //  permiso (`productos` lo lee cualquiera con
                           //  sesión), pero una celda en blanco no dice nada.
                           : `<span class="hint">producto #${d.producto_id}</span>`)
                         : veElConteo ? '<span class="hint">la línea del conteo ya no está</span>'
                         : '<span class="hint">no podés ver de qué producto es</span>'}</td>
        <td>${d.cantidad == null ? '—' : difTxt(d.cantidad)}${
              hecho ? ' <span class="chip c-ok">ya ajustado</span>' : ''}</td>
        <td>${esc(d.descripcion || '—')}</td>
        <td><span class="chip ${CHIP_DESVIO[d.estado] ?? 'c-pen'}">${esc(d.estado)}</span></td>
        <td>${esc(d.detecto || '—')}<div class="hint">${fecha(d.detectado_en)}</div></td>
        <td>${d.causa ? `${esc(d.causa)}<div class="hint">${esc(d.resolucion || '')} · ${esc(d.resolvio || '')}</div>` : '—'}</td>
        <td><div class="fila-acc">${
          abierto && resuelve ? `<button class="b b-pri" data-resolver="${d.id}">Resolver</button>` : ''}${
          ajustable ? `<button class="b b-sec" data-ajustar="${d.id}">Ajustar el stock</button>` : ''}${
          //  El botón que no está también afirma. Sin el producto no se puede ajustar (el formulario se llena con
          //  él), pero esconderlo se lee como "este desvío no se ajusta" cuando a ESTA persona le falta un permiso.
          //  La base lo dejaría: `ajustar_stock` es `security definer` y ve la línea.
          !ajustable && ajusta && !hecho && !d.producto_id && !veElConteo
            ? '<span class="hint">no se puede ajustar sin saber de qué producto es</span>' : ''}
        </div></td></tr>`;
  }).join('') || `<tr><td colspan="8" class="vacio">${
      alcance('desvio','ver') === 'todos'
        ? (verCerrados ? 'No hay desvíos de stock' : 'No hay desvíos de stock abiertos')
        //  La misma regla que el conteo, en el otro permiso: el RLS de `desvios` es `alcance('desvio','ver') =
        //  'todos'` O haberlo detectado o resuelto uno mismo, así que con alcance recortado "no hay" habla de una
        //  empresa que no se está mirando.
        : (verCerrados ? 'No hay desvíos de stock visibles para vos'
                       : 'No hay desvíos de stock abiertos visibles para vos')}</td></tr>`;
}
$('#dsVerCerrados').onchange = pintarDesvios;

$('#tblDesvios').onclick = e => {
  const bR = el(e.target).closest('[data-resolver]');
  const bA = el(e.target).closest('[data-ajustar]');
  if (bR) desplegarResolver(Number(bR.dataset.resolver), bR);
  if (bA) desplegarAjuste(Number(bA.dataset.ajustar), bA);
};

//  Resolver es supervisar: se escribe qué pasó y qué se hizo, y se elige si
//  el desvío queda RESUELTO (se encontró la causa) o ASUMIDO (no se
//  encontró y la empresa se lo come). Ninguna de las dos toca el número del
//  sistema — eso es el ajuste, y es otro acto (D65).
function desplegarResolver(id, btn){
  const d = DESVIOS.find(x => x.id === id);
  if (!d) return;
  const fila = btn.closest('tr');
  if (fila.nextElementSibling?.id === 'filaDesvio'){ fila.nextElementSibling.remove(); return; }
  document.getElementById('filaDesvio')?.remove();

  const tr = document.createElement('tr');
  tr.id = 'filaDesvio';
  tr.innerHTML = `<td colspan="8">
    <div class="confirmar">
      <div class="conf-tit">Resolver el desvío #${d.id}</div>
      <p class="conf-txt">${esc(d.descripcion || '')}<br><br>
         Cerrar el desvío <b>no cambia el stock del sistema</b>: informar y corregir son
         dos actos distintos. Si además hay que llevar el número a lo que se contó, eso es
         el botón <i>Ajustar el stock</i>, que deja su propio movimiento firmado.</p>
      <div class="grid g2">
        <label>Qué pasó <span class="hint">la causa</span>
          <input id="rdCausa" placeholder="Ej. se entregaron dos sin descargar del sistema"></label>
        <label>Qué se hizo <span class="hint">la resolución</span>
          <input id="rdResol" placeholder="Ej. se ajustó el stock y se avisó a logística"></label>
      </div>
      <div class="fila-acc" style="margin-top:0.588rem">
        <label class="hint">Cómo queda
          <select id="rdEstado">
            <option value="resuelto">Resuelto — se encontró la causa</option>
            <option value="asumido">Asumido — no se encontró y se da por perdido</option>
          </select></label>
        <button class="b b-sec" id="rdCancelar">Cancelar</button>
        <button class="b b-pri" id="rdOk">Cerrar el desvío</button>
      </div>
    </div></td>`;
  fila.after(tr);
  $('#rdCancelar', tr).onclick = () => tr.remove();
  $('#rdOk', tr).onclick = async () => {
    limpiar('#desviosMsg');
    const causa = $('#rdCausa', tr).value.trim();
    const resol = $('#rdResol', tr).value.trim();
    if (!causa || !resol){
      aviso('#desviosMsg','bad','Un desvío se cierra con la causa Y la resolución escritas. Las dos.');
      return;
    }
    const ok = $('#rdOk', tr); ok.disabled = true;
    try {
      //  `p_ajustar` va siempre en false: para un desvío de stock la base
      //  rechaza el ajuste automático, y está bien que lo rechace (D65).
      const { data, error } = await rpc('resolver_desvio', {
        p_desvio_id: d.id, p_causa: causa, p_resolucion: resol,
        p_ajustar: false, p_estado: $('#rdEstado', tr).value });
      if (error){ aviso('#desviosMsg','bad', error.message); return; }
      tr.remove();
      await cargarConteo();
      aviso('#desviosMsg','ok', data.mensaje);
    } finally { ok.disabled = false; }
  };
}

// ---------------------------------------------------------------------
//  Ajustar el stock  (D65)
// ---------------------------------------------------------------------
//  El select arranca SIN producto elegido: un `<select>` con `innerHTML` sin nada seleccionado queda en la
//  opción 0, y sin la primera opción vacía siempre habría un producto elegido aunque nadie lo tocara (con el
//  formulario precargado desde un desvío, un ajuste al producto equivocado).
function pintarProductosAjuste(){
  const elegido = $('#ajProducto').value;
  $('#ajProducto').innerHTML = '<option value="">Elegí el producto…</option>' +
    PRODUCTOS_AJ.map(p => `<option value="${p.id}">${esc(p.codigo)} — ${esc(p.descripcion)}</option>`).join('');
  //  Si lo que estaba elegido ya no está en la lista, se vuelve a vacío
  //  explícitamente: nunca se cae en "el primero de la lista".
  $('#ajProducto').value = (elegido && PRODUCTOS_AJ.some(p => String(p.id) === elegido)) ? elegido : '';
  //  Y el botón, que puede haber quedado apagado porque la base no contestó
  //  (ver el `submit` de `#formAjuste`). Lo que lo vuelve a encender es esto,
  //  que corre en cada `cargarConteo`: o sea, volver a entrar a la pestaña.
  $('#ajOk').disabled = false;
}

//  Ajustar desde un desvío: se precarga el producto y la diferencia del conteo (`cantidad` es `contado -
//  esperado`, la convención de `ajustar_stock`): ajustar por esa diferencia deja el sistema en lo contado.
//  Queda editable: quien ajusta puede haber averiguado otra explicación.
function desplegarAjuste(desvioId, btn){
  const d = DESVIOS.find(x => x.id === desvioId);
  if (!d) return;
  limpiar('#ajusteMsg');
  //  Si el producto del desvío está dado de baja no está en la lista, y llenar medio formulario con el select
  //  en blanco termina ajustando otro producto. Se avisa y no se llena nada.
  if (!d.producto_id || !PRODUCTOS_AJ.some(p => p.id === d.producto_id)){
    //  Se vacía TODO, no sólo el desvío: lo del desvío anterior bajo un cartel rojo que habla de éste termina
    //  ajustando el producto de la vez pasada.
    limpiarFormAjuste();
    aviso('#ajusteMsg','bad', `El producto de este desvío (${d.producto || 'sin código'}) no está en la lista de productos activos: puede estar dado de baja. No se puede ajustar desde acá.`);
    $('#cardAjustar').scrollIntoView({ behavior:'smooth', block:'center' });
    return;
  }
  const motivo = `Diferencia del conteo del ${d.conteo_fecha || ''}, desvío #${d.id}`;
  //  El motivo generado se guarda para poder distinguirlo después de uno que
  //  haya escrito la persona: al soltar el desvío hay que borrar el primero y
  //  respetar el segundo.
  AJUSTE_DE = { desvio: d.id, producto: d.producto_id, motivo };
  $('#ajProducto').value = String(d.producto_id);
  $('#ajCantidad').value = d.cantidad ?? '';
  $('#ajDesvio').value   = String(d.id);
  $('#ajMotivo').value   = motivo;
  $('#ajContexto').innerHTML = `<div class="aviso a-info">Ajustando por el desvío
    <b>#${d.id}</b>: ${esc(d.descripcion || '')}<br>
    Con este ajuste el sistema pasa a decir lo que se contó. El desvío no se cierra
    solo: cerralo aparte con su causa y su resolución.</div>`;
  $('#cardAjustar').scrollIntoView({ behavior:'smooth', block:'center' });
  if (btn) btn.blur();
}

//  Soltar el desvío: lo que quede ya no es "el ajuste de ese desvío". Un movimiento sobre el producto B atado
//  al desvío #12 (de A) deja el historial mintiendo; el motivo es parte del historial, así que si es el
//  generado solo y nombra ese desvío, se va con él. Uno escrito a mano se respeta.
function soltarAjuste(){
  if (AJUSTE_DE && $('#ajMotivo').value === AJUSTE_DE.motivo) $('#ajMotivo').value = '';
  AJUSTE_DE = null;
  $('#ajDesvio').value = '';
  $('#ajContexto').innerHTML = '';
}

function limpiarFormAjuste(){
  soltarAjuste();
  $('#ajProducto').value = '';
  $('#ajCantidad').value = '';
  $('#ajMotivo').value = '';
}

//  El formulario puede quedar abierto un rato largo: otro puede haber ajustado ese desvío o dado de baja el
//  producto, y seguir mostrando "ajustando por el desvío #12" con el botón habilitado es ajustar dos veces.
function revalidarAjuste(){
  if (!AJUSTE_DE) return;
  const sigue = PRODUCTOS_AJ.some(p => p.id === AJUSTE_DE.producto);
  const hecho = AJUSTADOS ? AJUSTADOS.has(AJUSTE_DE.desvio) : false;
  if (sigue && !hecho) return;
  const n = AJUSTE_DE.desvio;
  soltarAjuste();
  aviso('#ajusteMsg','warn', hecho
    ? `El desvío #${n} se ajustó mientras tenías el formulario abierto, así que lo solté: si lo mandabas, el stock quedaba corregido dos veces.`
    : `El producto del desvío #${n} se dio de baja mientras tenías el formulario abierto, así que lo solté.`);
}

$('#ajProducto').onchange = () => {
  if (AJUSTE_DE && Number($('#ajProducto').value) !== AJUSTE_DE.producto){
    soltarAjuste();
    aviso('#ajusteMsg','warn','Cambiaste de producto, así que este ajuste ya no queda atado al desvío. Si querés ajustar ése, volvé a apretar su botón.');
  }
};

$('#btnAjusteLimpiar').onclick = e => {
  e.preventDefault();
  limpiarFormAjuste();
  limpiar('#ajusteMsg');
};

$('#formAjuste').onsubmit = async e => {
  e.preventDefault();
  limpiar('#ajusteMsg');
  const cant   = $('#ajCantidad').value.trim();
  const motivo = $('#ajMotivo').value.trim();
  if (!$('#ajProducto').value){ aviso('#ajusteMsg','bad','Elegí un producto.'); return; }
  if (cant === '' || !entero(cant) || Number(cant) === 0){
    aviso('#ajusteMsg','bad','El ajuste es la diferencia a sumar o restar: un número entero, con signo, y distinto de cero. Si faltan dos unidades, es −2.');
    return;
  }
  if (!motivo){
    aviso('#ajusteMsg','bad','Escribí el motivo: es lo que se lee después para entender por qué el número cambió.');
    return;
  }
  const btn = $('#ajOk'); btn.disabled = true;
  //  Igual que los botones de plata de Comisiones: si la base no contestó no se sabe si el ajuste entró, y
  //  repetirlo corrige el stock DOS VECES (lo que `revalidarAjuste` evita entre personas). El botón queda
  //  apagado y lo vuelve a encender el repintado, o sea la recarga.
  let devolver = true;
  try {
    const { data, error } = await rpc('ajustar_stock', {
      p_producto_id: Number($('#ajProducto').value),
      p_cantidad   : Number(cant),
      p_motivo     : motivo,
      //  El desvío sale del estado del módulo, no del input escondido: es lo que la pantalla revalida, y un
      //  navegador que restaure el input dejaría el ajuste atado a un desvío que ya nadie mira.
      p_desvio_id  : (AJUSTE_DE && Number($('#ajProducto').value) === AJUSTE_DE.producto)
                       ? AJUSTE_DE.desvio : null
    });
    if (error){
      //  Los rechazos que la base marca con un código (0039) dejan esta pantalla mirando algo que ya no es
      //  cierto: el desvío que otro ajustó, el que quedó atado a otro producto y el que se quedó sin línea de
      //  conteo. En los tres se suelta el desvío y se vuelve a traer la lista (primero se recarga, después se
      //  avisa), o el botón repite el mismo error para siempre.
      if (['desvio_ya_ajustado','desvio_de_otro_producto','desvio_sin_producto'].includes(error.hint)){
        soltarAjuste();
        await cargarConteo();
      }
      if (error.code === RECHAZO) devolver = false;
      aviso('#ajusteMsg','bad', error.message);
      return;
    }
    limpiarFormAjuste();
    await cargarConteo();
    aviso('#ajusteMsg','ok', data.mensaje);
  } finally { if (devolver) btn.disabled = false; }
};
